import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  StatusFilterValue,
  TApiApplicationDocument,
  TApplicationStatus,
  TLocalFileEntry,
  TPageConfiguration,
  TSignatureValidationState,
} from '~/modules/covenants/types.ts';
import type {
  TFinancialConditionCertificateResponse,
  TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import type {
  TIncomeStructureByKvedCertificateResponse,
  TIncomeStructureByKvedFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';
import { DocListProvider } from '~/modules/covenants/CovenantsDataApplicationForm/components/DocListContext.tsx';
import { DocListGroupRows } from '~/modules/covenants/CovenantsDataApplicationForm/components/DocListGroupRows.tsx';
import { useGroupedDocuments } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useGroupedDocuments.ts';
import { TablePaginationControls } from '~/components/TablePaginationControls/TablePaginationControls.tsx';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { appFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/styles.ts';
import { APPLICATION_DOCUMENT_TABLE_LABELS } from '~/modules/covenants/CovenantsDataApplicationForm/constants.ts';
import { getApplicationDocumentTableHeaders } from '~/modules/covenants/CovenantsDataApplicationForm/utils.ts';

interface IDocListProps {
  contragentId: number;
  applicationStatus: TApplicationStatus;
  isNewApplication: boolean;
  applicationDocuments: TApiApplicationDocument[];
  page: number;
  rowsPerPage: number;
  pageConfiguration: TPageConfiguration;
  isLoading: boolean;
  showDeadlineColumn: boolean;
  localFiles: Record<string, TLocalFileEntry>;
  signatureValidationState: TSignatureValidationState;
  isReadOnly: boolean;
  onUploadForDoc: (docId: string) => void;
  onUploadSignatureClick: (docId: string, doc?: TApiApplicationDocument) => void;
  onDeleteDocument: (docId: string) => void;
  onDeleteSignature: (docId: string, doc?: TApiApplicationDocument) => void;
  onPreviewDocument: (doc: TApiApplicationDocument, localFile?: TLocalFileEntry) => void;
  onDownloadDocument: (doc: TApiApplicationDocument, localFile?: TLocalFileEntry) => void;
  onAddDocumentRow: (doc: TApiApplicationDocument) => void;
  onSaveFinancialCondition: (values: TFinancialConditionFormValues) => Promise<boolean>;
  onLoadFinancialCondition: (
    doc: TApiApplicationDocument
  ) => Promise<TFinancialConditionCertificateResponse | null>;
  onSaveIncomeStructureByKved: (
    doc: TApiApplicationDocument,
    values: TIncomeStructureByKvedFormValues
  ) => Promise<boolean>;
  onLoadIncomeStructureByKved: (
    doc: TApiApplicationDocument
  ) => Promise<TIncomeStructureByKvedCertificateResponse | null>;
  onGenerateDocument: (doc: TApiApplicationDocument, isFop?: boolean) => Promise<boolean>;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

const areSetsEqual = (left: Set<string>, right: Set<string>): boolean =>
  left.size === right.size && Array.from(left).every((key) => right.has(key));

export const DocList: React.FC<IDocListProps> = ({
  contragentId,
  applicationStatus,
  isNewApplication,
  applicationDocuments,
  page,
  rowsPerPage,
  pageConfiguration,
  isLoading,
  showDeadlineColumn,
  localFiles,
  signatureValidationState,
  isReadOnly,
  onUploadForDoc,
  onUploadSignatureClick,
  onDeleteDocument,
  onDeleteSignature,
  onPreviewDocument,
  onDownloadDocument,
  onAddDocumentRow,
  onSaveFinancialCondition,
  onLoadFinancialCondition,
  onSaveIncomeStructureByKved,
  onLoadIncomeStructureByKved,
  onGenerateDocument,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const tableHeaders = getApplicationDocumentTableHeaders(showDeadlineColumn);
  const groupedDocuments = useGroupedDocuments(applicationDocuments, localFiles);
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    const visibleKeys = new Set(groupedDocuments.map((group) => group.key));
    const deniedGroupKeys = new Set(
      groupedDocuments
        .filter((group) =>
          group.fileRows.some((fileRow) => fileRow.status === StatusFilterValue.Denied)
        )
        .map((group) => group.key)
    );

    setExpandedGroupKeys((prev) => {
      const next = new Set(Array.from(prev).filter((key) => visibleKeys.has(key)));

      deniedGroupKeys.forEach((key) => next.add(key));

      return areSetsEqual(prev, next) ? prev : next;
    });
  }, [groupedDocuments]);

  const contextValue = useMemo(
    () => ({
      contragentId,
      applicationStatus,
      isNewApplication,
      showDeadlineColumn,
      localFiles,
      signatureValidationState,
      isReadOnly,
      onUploadForDoc,
      onUploadSignatureClick,
      onDeleteDocument,
      onDeleteSignature,
      onPreviewDocument,
      onDownloadDocument,
      onAddDocumentRow,
      onSaveFinancialCondition,
      onLoadFinancialCondition,
      onSaveIncomeStructureByKved,
      onLoadIncomeStructureByKved,
      onGenerateDocument,
    }),
    [
      contragentId,
      applicationStatus,
      isNewApplication,
      showDeadlineColumn,
      localFiles,
      signatureValidationState,
      isReadOnly,
      onUploadForDoc,
      onUploadSignatureClick,
      onDeleteDocument,
      onDeleteSignature,
      onPreviewDocument,
      onDownloadDocument,
      onAddDocumentRow,
      onSaveFinancialCondition,
      onLoadFinancialCondition,
      onSaveIncomeStructureByKved,
      onLoadIncomeStructureByKved,
      onGenerateDocument,
    ]
  );

  const toggleGroup = (groupKey: string) => {
    setExpandedGroupKeys((prev) => {
      const next = new Set(prev);

      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }

      return next;
    });
  };

  return (
    <DocListProvider value={contextValue}>
      <>
        <Box sx={appFormStyles.docsList}>
          <Box sx={commonStyles.loadingWrap}>
            <TableContainer component={Paper} elevation={0} sx={appFormStyles.docsTable}>
              <Table
                sx={appFormStyles.table}
                aria-label={APPLICATION_DOCUMENT_TABLE_LABELS.ariaLabel}
              >
                <TableHead sx={appFormStyles.docsHead}>
                  <TableRow>
                    {tableHeaders.map((header: (typeof tableHeaders)[number]) => (
                      <TableCell
                        key={header.id}
                        align={header.align}
                        sx={{ ...appFormStyles.docsHeaderCell, ...header.sx }}
                      >
                        {header.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody sx={appFormStyles.docsBody}>
                  {groupedDocuments.map((group, index) => (
                    <DocListGroupRows
                      key={group.key}
                      group={group}
                      isLastGroup={index === groupedDocuments.length - 1}
                      isExpanded={expandedGroupKeys.has(group.key)}
                      onToggleExpand={() => toggleGroup(group.key)}
                    />
                  ))}

                  {!applicationDocuments.length && (
                    <TableRow>
                      <TableCell
                        colSpan={tableHeaders.length}
                        align="center"
                        sx={appFormStyles.docsEmptyCell}
                      >
                        <Typography variant="body1" color="text.secondary">
                          {APPLICATION_DOCUMENT_TABLE_LABELS.empty}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {isLoading && (
              <Box sx={commonStyles.loadingMask}>
                <CircularProgress size={28} />
              </Box>
            )}
          </Box>
        </Box>

        {pageConfiguration.totalElements > 0 && (
          <TablePaginationControls
            page={page}
            rowsPerPage={rowsPerPage}
            totalElements={pageConfiguration.totalElements}
            isLoading={isLoading}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        )}
      </>
    </DocListProvider>
  );
};
